import React, { useEffect, useState } from "react";
import { Loader2, Package, Play, Sparkles, RotateCw } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Phase 27c · Admin Video V2 Packs tab.
 * List monthly aggregates + publish premium + unpublish + regenerate cover.
 */
export default function AdminVideosV2Packs() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aggregating, setAggregating] = useState(false);

  const fetchPacks = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/videos/v2/packs?limit=50`, {
        credentials: "include",
      });
      if (r.ok) {
        const d = await r.json();
        setPacks(d.items || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPacks(); }, []);

  const triggerAggregation = async () => {
    setAggregating(true);
    try {
      const r = await fetch(`${API}/api/admin/videos/v2/packs/aggregate/trigger`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Aggregated: +${d.created || 0} created, +${d.upgraded || 0} upgraded`);
        await fetchPacks();
      } else {
        toast.error(d?.detail?.error || "Aggregation failed");
      }
    } finally {
      setAggregating(false);
    }
  };

  const publishPremium = async (packId, videoCount) => {
    const suggested = Math.min(1900 + videoCount * 100, 4900);
    const priceInput = window.prompt(
      `Publish as premium marketplace asset.\nSuggested: $${(suggested / 100).toFixed(2)}\n\nEnter price in EUR cents (1900-4900):`,
      String(suggested),
    );
    if (!priceInput) return;
    const price = parseInt(priceInput, 10);
    if (Number.isNaN(price)) return toast.error("Invalid price");
    try {
      const r = await fetch(`${API}/api/admin/videos/v2/packs/${packId}/publish-premium`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_cents: price }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Published pack — asset_id: ${d.asset_id.slice(0, 8)}…`);
        await fetchPacks();
      } else {
        toast.error(d?.detail?.error || "Publish failed");
      }
    } catch { toast.error("Network error"); }
  };

  const unpublish = async (packId) => {
    if (!window.confirm("Delist this pack from the marketplace?")) return;
    try {
      const r = await fetch(`${API}/api/admin/videos/v2/packs/${packId}/unpublish`, {
        method: "POST", credentials: "include",
      });
      if (r.ok) { toast.success("Pack unpublished"); await fetchPacks(); }
      else toast.error("Unpublish failed");
    } catch { toast.error("Network error"); }
  };

  const regenCover = async (packId) => {
    try {
      const r = await fetch(`${API}/api/admin/videos/v2/packs/${packId}/regenerate-cover`, {
        method: "POST", credentials: "include",
      });
      if (r.ok) { toast.success("Cover regenerated"); await fetchPacks(); }
      else toast.error("Cover regen failed");
    } catch { toast.error("Network error"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-videos-v2-packs">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold inline-flex items-center gap-2">
          <Package className="h-4 w-4" /> Video V2 Packs · Monthly Aggregates
        </h2>
        <button
          data-testid="admin-pack-v2-aggregate-btn"
          onClick={triggerAggregation}
          disabled={aggregating}
          className="text-xs px-3 py-1.5 rounded-full bg-white hover:bg-[#EDEDED] text-black inline-flex items-center gap-1 disabled:opacity-50"
        >
          {aggregating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          Trigger aggregation
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-text-secondary text-xs">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading packs…
        </div>
      ) : packs.length === 0 ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center text-sm text-text-secondary">
          No V2 packs yet. Trigger the aggregation cron after 15+ rendered videos per creator.
        </div>
      ) : (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
          <table className="w-full text-xs" data-testid="admin-v2-packs-table">
            <thead className="bg-[var(--color-bg)]/60 text-text-secondary uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2">Pack</th>
                <th className="text-left px-4 py-2">Videos</th>
                <th className="text-left px-4 py-2">Price</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {packs.map((p) => (
                <tr key={p.pack_id} className="border-t border-[var(--color-border)]"
                    data-testid={`admin-pack-row-${p.pack_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--color-text-main)]">{p.title}</div>
                    <div className="text-[10px] text-text-secondary">{p.month_yyyy_mm} · {p.virtual_creator_id}</div>
                  </td>
                  <td className="px-4 py-3">{p.total_videos_count || (p.video_ids || []).length}</td>
                  <td className="px-4 py-3">${((p.pack_v2_price_cents || 0) / 100).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    {p.is_monetized ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--color-success)]/20 text-[var(--color-success)] border border-[var(--color-success)]/40">
                        Monetized
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-text-secondary border border-[var(--color-border)]">
                        Aggregated
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      data-testid={`admin-pack-regen-cover-${p.pack_id}`}
                      onClick={() => regenCover(p.pack_id)}
                      className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[var(--color-border)] hover:border-[var(--color-secondary)] inline-flex items-center gap-1"
                    >
                      <RotateCw className="h-2.5 w-2.5" /> cover
                    </button>
                    {p.is_monetized ? (
                      <button
                        data-testid={`admin-pack-unpublish-${p.pack_id}`}
                        onClick={() => unpublish(p.pack_id)}
                        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[var(--color-alert)]/50 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10"
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-pack-publish-premium-${p.pack_id}`}
                        onClick={() => publishPremium(p.pack_id, p.total_videos_count || (p.video_ids || []).length)}
                        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white hover:bg-[#EDEDED] text-black"
                      >
                        Publish premium
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
